import type {
  AuthStartResponse,
  AuthVerifyResponse,
  BookChunkSummary,
  BookCoverageResponse,
  BookDetailResponse,
  BookSummary,
  CardRating,
  ChatInput,
  ChatResponse,
  CheckoutResponse,
  DictionaryInput,
  DictionaryResponse,
  GenerateLessonResponse,
  GoogleAuthInput,
  ImportBookInput,
  ImportBookResponse,
  ImportTextBookInput,
  ImportTranslatedBookInput,
  JobSummary,
  LessonDetail,
  LessonResultResponse,
  LessonSummary,
  ListResponse,
  MeResponse,
  MinedCardsResponse,
  MinedDeckSummary,
  MinedReviewResponse,
  ReadingProgressSummary,
  SelfRating,
  SubscriptionResponse,
  UpdateBookInput,
} from "./types";

export class LumiApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "LumiApiError";
    this.status = status;
    this.code = code;
  }
}

export interface LumiApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  csrfToken?: string | null;
}

export class LumiApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private csrfToken: string | null;

  constructor(options: LumiApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? defaultBaseUrl()).replace(/\/$/, "");
    this.fetchImpl = options.fetchImpl ?? defaultFetch();
    this.csrfToken = options.csrfToken ?? csrfTokenFromCookie();
  }

  setCsrfToken(csrfToken: string | null) {
    this.csrfToken = csrfToken;
  }

  me() {
    return this.request<MeResponse>("/api/v1/me");
  }

  startAuth(email: string, displayName?: string) {
    return this.request<AuthStartResponse>("/api/v1/auth/start", {
      method: "POST",
      body: {
        email,
        display_name: displayName,
      },
      csrf: false,
    });
  }

  async verifyAuth(email: string, code: string) {
    const response = await this.request<AuthVerifyResponse>("/api/v1/auth/verify", {
      method: "POST",
      body: { email, code },
      csrf: false,
    });

    this.csrfToken = response.csrf_token;
    return response;
  }

  async authenticateWithGoogle(input: GoogleAuthInput) {
    const response = await this.request<AuthVerifyResponse>("/api/v1/auth/google", {
      method: "POST",
      body: input,
      csrf: false,
    });

    this.csrfToken = response.csrf_token;
    return response;
  }

  logout() {
    return this.request<{ status: "ok" }>("/api/v1/auth/logout", {
      method: "POST",
      csrf: false,
    });
  }

  /** `kind` narrows the listing to books or saved articles; omit for both. */
  listBooks(kind?: "book" | "article") {
    const query = kind ? `?kind=${kind}` : "";
    return this.request<ListResponse<BookSummary>>(`/api/v1/books${query}`);
  }

  importBook(input: ImportBookInput) {
    return this.request<ImportBookResponse>("/api/v1/books/import", {
      method: "POST",
      body: input,
    });
  }

  importTextBook(input: ImportTextBookInput) {
    return this.request<ImportBookResponse>("/api/v1/books/import-text", {
      method: "POST",
      body: input,
    });
  }

  importTranslatedBook(input: ImportTranslatedBookInput) {
    return this.request<ImportBookResponse>("/api/v1/books/import-translated", {
      method: "POST",
      body: input,
    });
  }

  // Multipart upload (PDF/.txt). Bypasses request() so the browser sets the
  // multipart boundary; still sends the CSRF header + session cookie.
  async uploadBook(form: FormData): Promise<ImportBookResponse> {
    const headers = new Headers();
    if (this.csrfToken) {
      headers.set("x-lumi-csrf", this.csrfToken);
    }

    const response = await this.fetchImpl(`${this.baseUrl}/api/v1/books/upload`, {
      method: "POST",
      headers,
      credentials: "include",
      body: form,
    });

    if (!response.ok) {
      throw await toApiError(response);
    }

    return (await response.json()) as ImportBookResponse;
  }

  getBook(bookId: string) {
    return this.request<BookDetailResponse>(`/api/v1/books/${bookId}`);
  }

  updateBook(bookId: string, input: UpdateBookInput) {
    return this.request<{ book: BookSummary }>(`/api/v1/books/${bookId}`, {
      method: "PATCH",
      body: input,
    });
  }

  deleteBook(bookId: string) {
    return this.request<{ status: "ok" }>(`/api/v1/books/${bookId}`, {
      method: "DELETE",
    });
  }

  listBookChunks(bookId: string) {
    return this.request<ListResponse<BookChunkSummary>>(
      `/api/v1/books/${bookId}/chunks`,
    );
  }

  updateBookProgress(bookId: string, chunkIndex: number) {
    return this.request<{ progress: ReadingProgressSummary }>(
      `/api/v1/books/${bookId}/progress`,
      {
        method: "PATCH",
        body: { chunk_index: chunkIndex },
      },
    );
  }

  listJobs() {
    return this.request<ListResponse<JobSummary>>("/api/v1/jobs");
  }

  getJob(jobId: string) {
    return this.request<JobSummary>(`/api/v1/jobs/${jobId}`);
  }

  createDictionaryEntry(input: DictionaryInput) {
    return this.request<DictionaryResponse>("/api/v1/dictionary", {
      method: "POST",
      body: input,
    });
  }

  // Free/Pro state for the signed-in user. Works even when Stripe is not
  // configured server-side (the backend reads its local subscriptions table).
  getSubscription() {
    return this.request<SubscriptionResponse>("/api/v1/billing/subscription");
  }

  // Returns a Stripe Checkout URL for new subscribers or a Billing Portal URL
  // for existing customers. The backend responds 501 when Stripe is not
  // configured (surface via LumiApiError.status).
  startCheckout(billingPeriod: "monthly" | "yearly") {
    return this.request<CheckoutResponse>(
      `/api/v1/billing/checkout?billingPeriod=${billingPeriod}`,
      { method: "POST" },
    );
  }

  createChatMessage(input: ChatInput) {
    return this.request<ChatResponse>("/api/v1/chat", {
      method: "POST",
      body: input,
    });
  }

  // ── Lessons (generative audio drills) ────────────────────────────────────

  listLessons(language: string) {
    const q = encodeURIComponent(language);
    return this.request<ListResponse<LessonSummary>>(
      `/api/v1/me/lessons?language=${q}`,
    );
  }

  getLesson(lessonId: string) {
    return this.request<LessonDetail>(`/api/v1/me/lessons/${lessonId}`);
  }

  postLessonResult(lessonId: string, selfRating: SelfRating) {
    return this.request<LessonResultResponse>(
      `/api/v1/me/lessons/${lessonId}/result`,
      {
        method: "POST",
        body: { self_rating: selfRating },
      },
    );
  }

  /** Enqueue generation; 202 + job id. */
  generateLesson(language: string) {
    return this.request<GenerateLessonResponse>("/api/v1/me/lessons/generate", {
      method: "POST",
      body: { language },
    });
  }

  /** Known-lemma coverage for a book (null fields when unavailable). */
  getBookCoverage(bookId: string) {
    return this.request<BookCoverageResponse>(
      `/api/v1/books/${bookId}/coverage`,
    );
  }

  // ── Sentence mining (i+1 flashcards from captured sentences) ─────────────

  listMinedDecks() {
    return this.request<ListResponse<MinedDeckSummary>>("/api/v1/mine/decks");
  }

  getMinedDeck(deckId: string) {
    return this.request<MinedDeckSummary>(`/api/v1/mine/decks/${deckId}`);
  }

  /**
   * Ranked study cards. `mode` is `study` (new + due, best first), `i1`
   * (only one-unknown-word sentences), `due`, or `all`.
   */
  listMinedCards(
    deckId: string,
    options: { limit?: number; mode?: "study" | "i1" | "due" | "all" } = {},
  ) {
    const params = new URLSearchParams();
    if (options.limit) params.set("limit", String(options.limit));
    if (options.mode) params.set("mode", options.mode);
    const query = params.toString();
    return this.request<MinedCardsResponse>(
      `/api/v1/mine/decks/${deckId}/cards${query ? `?${query}` : ""}`,
    );
  }

  /**
   * Rate a card. `targetLemma` is sent back so the credit lands on the word
   * the learner actually saw, even if the ranking has since moved on.
   */
  reviewMinedCard(
    sentenceId: string,
    rating: CardRating,
    targetLemma?: string | null,
  ) {
    return this.request<MinedReviewResponse>(
      `/api/v1/mine/cards/${sentenceId}/review`,
      {
        method: "POST",
        body: { rating, target_lemma: targetLemma ?? null },
      },
    );
  }

  /** Drop a sentence from its deck — for lines that are not sentences. */
  deleteMinedCard(sentenceId: string) {
    return this.request<void>(`/api/v1/mine/cards/${sentenceId}`, {
      method: "DELETE",
    });
  }

  private async request<T>(
    path: string,
    options: {
      method?: string;
      body?: unknown;
      csrf?: boolean;
    } = {},
  ): Promise<T> {
    const headers = new Headers();

    if (options.body !== undefined) {
      headers.set("content-type", "application/json");
    }

    if (options.csrf !== false && this.csrfToken) {
      headers.set("x-lumi-csrf", this.csrfToken);
    }

    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method: options.method ?? "GET",
      headers,
      credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

    if (!response.ok) {
      throw await toApiError(response);
    }

    // 204 has no body; parsing it as JSON throws on an otherwise fine request.
    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}

export function createLumiApiClient(options: LumiApiClientOptions = {}) {
  return new LumiApiClient(options);
}

function defaultBaseUrl() {
  if (typeof window !== "undefined") {
    return process.env.NEXT_PUBLIC_LUMI_API_BASE_URL ?? "";
  }

  return process.env.LUMI_API_BASE_URL ?? "http://127.0.0.1:4000";
}

function defaultFetch(): typeof fetch {
  const fetchTarget = typeof window === "undefined" ? globalThis : window;
  return fetchTarget.fetch.bind(fetchTarget);
}

function csrfTokenFromCookie() {
  if (typeof document === "undefined") {
    return null;
  }

  return (
    document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("lumi_csrf="))
      ?.slice("lumi_csrf=".length) ?? null
  );
}

async function toApiError(response: Response) {
  let message = `Lumi API request failed with status ${response.status}`;
  let code: string | undefined;

  try {
    const body = (await response.json()) as { error?: string; message?: string };
    message = body.message ?? message;
    code = body.error;
  } catch {
    // Keep the status-only message when the response is not JSON.
  }

  return new LumiApiError(message, response.status, code);
}
